import { ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "@/lib/nav";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { addDays, type ChartRange, type ChartSpan } from "@buildpy/core/modules/project/phase-chart";
import {
  BAR_MIN_DAY_PX,
  barLabelFits,
  SITE_CHART_HEAD_ROWS,
  todaySideOf,
  type SiteChart as SiteChartData,
  type SitePhaseRow,
  type SitePhaseState,
  type SiteRow,
} from "@buildpy/core/modules/project/site-chart";

// 칸 폭 — 석 달(최대 92일)×9px 을 밑으로 두고 좁은 화면은 차트 영역만 가로로 민다. md 미만(폰)은 오른쪽 칸 없이 두 칸, lg 는 날짜 영역을 420px(SITE_CHART_MIN_TRACK_PX)까지 줄인다.
export const GRID_CLASS =
  "grid grid-cols-[7rem_minmax(0,1fr)] min-w-[calc(7rem+828px)] md:grid-cols-[13rem_minmax(0,1fr)_7.5rem] md:min-w-[calc(20.5rem+828px)] lg:min-w-[calc(20.5rem+420px)]";

// 오른쪽 「공정 · 준공」 칸 — md 이상에서만, 가로 스크롤 중에도 오른쪽에 붙어 있다(폰은 이름 칸 아래 줄로 옮긴다).
const RIGHT_CELL = "hidden bg-card md:sticky md:right-0 md:z-[2] md:flex";

// 준공이 창 뒤일 때 오른쪽 끝 「n월 →」 자리(rem) — 진행 글자가 이만큼 비운다. CSS 와 판정이 이 한 값을 쓴다(판정은 실제 루트 글자 크기로 px 환산).
const END_AFTER_ROOM_REM = 3.25;
const END_AFTER_ROOM = `${END_AFTER_ROOM_REM}rem`;
// 루트 글자 크기를 못 잴 때(첫 렌더·SSR·테스트) 쓰는 브라우저 기본값.
export const DEFAULT_REM_PX = 16;
// 준공 ◆(size-3 을 45° 돌려 외접 약 17px · 반폭 8.5px) — 반폭은 9px 로 잡는다.
const END_MARK_HALF_PX = 9;
// ◆ 에서 글자를 띄울 거리 — 반폭 9px + 간격 4px.
const END_MARK_CLEAR_PX = END_MARK_HALF_PX + 4;
const END_MARK_CLEAR = `${END_MARK_CLEAR_PX}px`;

// ◆ 중심 — 칸 가운데를 트랙 양 끝에서 반폭 안쪽으로 묶어 창 처음·마지막 날에도 트랙 밖으로 안 나간다(fromRight 는 오른쪽 끝에서 잰 거리).
export const endMarkAt = (endCol: number, range: ChartRange, fromRight = false): string =>
  `clamp(${END_MARK_HALF_PX}px, ${pct(fromRight ? range.days - endCol - 0.5 : endCol + 0.5, range)}, calc(100% - ${END_MARK_HALF_PX}px))`;

// 같은 ◆ 중심을 칸 단위로(하루 폭 dayPx 기준) — 글자 자리 폭 판정용.
const endMarkCol = (endCol: number, days: number, dayPx: number): number =>
  Math.min(Math.max(endCol + 0.5, END_MARK_HALF_PX / dayPx), days - END_MARK_HALF_PX / dayPx);

// 진행 글자 자리 — end 는 준공 ◆ 와의 관계: 없음(◆ 가 글자 자리 밖) · room(오늘과 ◆ 사이, ◆ 앞 빈 자리) · past(◆ 너머) · flip(오늘 반대쪽). side 는 글자가 오늘 선의 어느 쪽인지.
export type LabelSlot = { end: "room" | "past" | "flip" | undefined; side: "before" | "after"; left: string; right: string; px: number };

// 글자 상자 한쪽 끝 — css 는 CSS left/right 값, px 는 트랙 왼쪽에서 잰 자리.
type Edge = { css: string; px: number };

// 진행 글자 상자 — ◆ 가 글자 자리 밖이면 그대로 · 안이면 ◆ 앞(room) → ◆ 너머(past) → 오늘 반대쪽(flip) 중 글자가 드는 첫 자리, 다 좁으면 가장 넓은 자리(말줄임). 오늘 뒤 글자는 같은 줄 예정 막대(plan) 가장자리를 넘지 않는다. trackPx 를 못 재면 가장 좁은 트랙으로 본다.
export const progressLabelBox = (box: {
  label: string;
  todayCol: number;
  endCol: number | null;
  range: ChartRange;
  endAfter: boolean;
  plan?: ChartSpan | null;
  trackPx?: number | null;
  remPx?: number | null;
}): LabelSlot => {
  const { range, todayCol, endCol } = box;
  const days = range.days;
  const dayPx = box.trackPx && box.trackPx > 0 ? box.trackPx / days : BAR_MIN_DAY_PX;
  const remPx = box.remPx && box.remPx > 0 ? box.remPx : DEFAULT_REM_PX;
  const trackPx = days * dayPx;
  const fromRight = (css: string, distPx: number): Edge => ({ css, px: trackPx - distPx });
  const join = (edges: Edge[]): string => (edges.length === 1 ? edges[0]!.css : `max(${edges.map((e) => e.css).join(", ")})`);
  // 자리 하나 — 왼쪽 끝은 가장 오른쪽 것, 오른쪽 끝은 가장 왼쪽 것(CSS 는 둘 다 max), 폭은 그 사이.
  const slot = (end: LabelSlot["end"], side: LabelSlot["side"], lefts: (Edge | null)[], rights: (Edge | null)[]): LabelSlot => {
    const ls = lefts.filter((e): e is Edge => e !== null);
    const rs = rights.filter((e): e is Edge => e !== null);
    return { end, side, left: join(ls), right: join(rs), px: Math.min(...rs.map((e) => e.px)) - Math.max(...ls.map((e) => e.px)) };
  };
  const leftEdge: Edge = { css: "4px", px: 4 };
  const rightEdge = fromRight("4px", 4);
  const monthRoom = box.endAfter ? fromRight(END_AFTER_ROOM, END_AFTER_ROOM_REM * remPx) : null;
  const afterTodayEdge: Edge = { css: `calc(${pct(todayCol + 1, range)} + 4px)`, px: (todayCol + 1) * dayPx + 4 };
  const beforeTodayEdge = fromRight(`calc(${pct(days - todayCol, range)} + 4px)`, (days - todayCol) * dayPx + 4);
  const mark = endCol === null ? null : endMarkCol(endCol, days, dayPx);
  const pastMark: Edge | null =
    endCol === null || mark === null ? null : { css: `calc(${endMarkAt(endCol, range)} + ${END_MARK_CLEAR})`, px: mark * dayPx + END_MARK_CLEAR_PX };
  const beforeMark =
    endCol === null || mark === null ? null : fromRight(`calc(${endMarkAt(endCol, range, true)} + ${END_MARK_CLEAR})`, trackPx - mark * dayPx + END_MARK_CLEAR_PX);
  // 예정 막대가 오늘 뒤 글자 시작보다 뒤에서 시작하면(착공 전에 착수) 글자는 막대 4px 앞에서 끝난다 · ◆ 너머 글자는 막대 끝 4px 뒤부터.
  const plan = box.plan ?? null;
  const planStart = plan && plan.from > todayCol + 1 ? fromRight(`calc(${pct(days - plan.from, range)} + 4px)`, (days - plan.from) * dayPx + 4) : null;
  const planEnd: Edge | null = plan ? { css: `calc(${pct(plan.from + plan.span, range)} + 4px)`, px: (plan.from + plan.span) * dayPx + 4 } : null;
  // 예정 막대가 오늘 칸보다 앞에서 시작하면(착수가 오늘 전) 오늘 앞 글자도 막대 4px 앞에서 끝난다.
  const planBefore = plan && plan.from < todayCol ? fromRight(`calc(${pct(days - plan.from, range)} + 4px)`, (days - plan.from) * dayPx + 4) : null;
  // 오늘 앞·뒤 자리도 ◆ 쪽 끝을 ◆ 중심 13px 앞에서 자른다 — ◆ 가 오늘 선에 붙거나 걸쳐도 글자가 ◆ 를 덮지 않는다.
  const beforeToday = (end: LabelSlot["end"]) => slot(end, "before", [leftEdge], [beforeTodayEdge, monthRoom, beforeMark, planBefore]);
  const afterToday = (end: LabelSlot["end"]) => slot(end, "after", [afterTodayEdge, pastMark], [rightEdge, monthRoom, planStart]);
  const fits = (s: LabelSlot) => barLabelFits(box.label, (s.px + 12) / BAR_MIN_DAY_PX);
  const pick = (slots: LabelSlot[]) => slots.find(fits) ?? slots.reduce((a, b) => (b.px > a.px ? b : a));
  const before = todaySideOf(todayCol, days) === "before";
  if (endCol === null || mark === null || (before ? endCol >= todayCol : endCol <= todayCol)) {
    if (before) return beforeToday(undefined);
    // 예정 막대 앞이 좁으면 오늘 앞으로 넘긴다.
    return planStart ? pick([afterToday(undefined), beforeToday(undefined)]) : afterToday(undefined);
  }
  const slots: LabelSlot[] = before
    ? [slot("room", "before", [pastMark], [beforeTodayEdge, monthRoom]), slot("past", "before", [leftEdge], [beforeMark]), afterToday("flip")]
    : [
        slot("room", "after", [afterTodayEdge], [beforeMark, planStart]),
        slot("past", "after", [pastMark, planEnd], [rightEdge, monthRoom]),
        beforeToday("flip"),
      ];
  // barLabelFits 는 좌우 여백 12px 를 더해 BAR_MIN_DAY_PX 칸으로 재므로 px 를 그 칸 수로 되돌려 묻는다(하루 폭과 무관).
  return pick(slots);
};

const TONE_CLASS: Record<SitePhaseState, string> = {
  done: "bg-status-done-bg text-status-done",
  now: "bg-primary font-semibold text-primary-foreground",
  hold: "bg-status-hold-bg text-status-hold",
  plan: "bg-accent text-accent-foreground",
  late: "bg-card font-semibold text-destructive ring-1 ring-inset ring-destructive",
};

const LEGEND: { state: SitePhaseState; label: string }[] = [
  { state: "done", label: "끝난 공정" },
  { state: "now", label: "진행 중" },
  { state: "hold", label: "보류" },
  { state: "plan", label: "예정" },
  { state: "late", label: "지연" },
];

const STATE_TEXT_CLASS: Record<SitePhaseState, string> = {
  done: "text-muted-foreground",
  now: "text-primary",
  hold: "text-status-hold",
  plan: "text-muted-foreground",
  late: "text-destructive",
};

const pct = (n: number, range: ChartRange): string => `${(n / range.days) * 100}%`;

// 칸 자리는 CSS 변수로 넘기고 클래스가 읽는다(left-[var(--left)] w-[var(--w)] 등).
const SPAN_CLASS = "left-[var(--left)] w-[var(--w)]";

const todayLeft = (col: number, range: ChartRange): string => `calc(${pct(col + 0.5, range)} - 1px)`;

// 머리 칸 한 줄 자리 — top·높이를 못박고 줄 높이도 같은 16px(leading-4, SITE_CHART_HEAD_ROWS 의 height)로 맞춰 글자 줄 상자가 넘치지 않는다.
const HEAD_ROW_CLASS = "top-[var(--top)] flex h-[var(--h)] items-center leading-4";

// 가로 스크롤 자리 — 오늘이 창 밖이면 처음(0), 넘치지 않으면 그대로(null), 아니면 오늘 선을 이름 칸 뒤 1/3 자리에.
export const chartScrollTarget = (box: {
  todayCol: number | null;
  markerX: number | null;
  nameWidth: number;
  clientWidth: number;
  scrollWidth: number;
}): number | null => {
  if (box.todayCol === null || box.markerX === null) return 0;
  if (box.scrollWidth <= box.clientWidth) return null;
  return Math.round(Math.max(0, box.markerX - box.nameWidth - (box.clientWidth - box.nameWidth) / 3));
};

export interface TrackSize {
  trackPx: number;
  remPx: number;
}

// 재기·구독에 쓰는 브라우저 몫 — 테스트가 가짜를 넣는다.
export interface TrackSizeEnv {
  win: Pick<Window, "addEventListener" | "removeEventListener" | "getComputedStyle">;
  root: Element;
  ResizeObserver?: typeof ResizeObserver;
}

// 날짜 영역 폭·루트 글자 크기를 재고 바뀔 때마다 onSize — 트랙(ResizeObserver)·창 크기(resize, RO 가 늦거나 없을 때 대비)·루트 글자 크기 변화를 다 듣는다. 반환값은 구독 해제.
export const watchTrackSize = (el: Element, onSize: (size: TrackSize | null) => void, env: TrackSizeEnv): (() => void) => {
  let last: TrackSize | null | undefined;
  const measure = () => {
    const trackPx = el.getBoundingClientRect().width;
    const remPx = Number.parseFloat(env.win.getComputedStyle(env.root).fontSize) || DEFAULT_REM_PX;
    const next = trackPx > 0 ? { trackPx, remPx } : null;
    if (last !== undefined && (last === next || (last && next && last.trackPx === next.trackPx && last.remPx === next.remPx))) return;
    last = next;
    onSize(next);
  };
  measure();
  env.win.addEventListener("resize", measure);
  const observer = env.ResizeObserver ? new env.ResizeObserver(measure) : null;
  observer?.observe(el);
  observer?.observe(env.root);
  return () => {
    env.win.removeEventListener("resize", measure);
    observer?.disconnect();
  };
};

// 날짜 영역 실제 폭·루트 글자 크기 — 재기 전(첫 렌더·SSR)은 null 이라 글자 자리 판정이 가장 좁은 트랙 기준으로 간다.
const useTrackSize = (ref: RefObject<HTMLDivElement | null>): TrackSize | null => {
  const [size, setSize] = useState<TrackSize | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return watchTrackSize(el, setSize, {
      win: window,
      root: document.documentElement,
      ResizeObserver: typeof ResizeObserver === "undefined" ? undefined : ResizeObserver,
    });
  }, [ref]);
  return size;
};

interface TrackProps {
  chart: SiteChartData;
  withToday: boolean;
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  measureRef?: RefObject<HTMLDivElement | null>;
}

// 한 줄의 시간 축 — 공휴일 띠·주 눈금·오늘 선을 줄마다 같은 자리에 그려 세로로 이어 보이게 한다.
const Track = ({ chart, withToday, children, className, onClick, measureRef }: TrackProps) => (
  <div ref={measureRef} className={cn("relative border-b border-border", onClick && "cursor-pointer", className)} onClick={onClick}>
    {chart.holidayCols.map((col) => (
      <span
        key={`h${col}`}
        aria-hidden
        className={cn("absolute inset-y-0 bg-muted", SPAN_CLASS)}
        style={{ "--left": pct(col, chart.range), "--w": pct(1, chart.range) } as CSSProperties}
      />
    ))}
    {chart.ticks.map((tick) => (
      <span
        key={`w${tick.col}`}
        aria-hidden
        className="absolute inset-y-0 left-[var(--left)] w-px bg-border/50"
        style={{ "--left": pct(tick.col, chart.range) } as CSSProperties}
      />
    ))}
    {chart.months.map((month) => (
      <span
        key={`m${month.col}`}
        aria-hidden
        className="absolute inset-y-0 left-[var(--left)] w-px bg-border"
        style={{ "--left": pct(month.col, chart.range) } as CSSProperties}
      />
    ))}
    {children}
    {withToday && chart.todayCol !== null && (
      <span
        aria-hidden
        className="absolute inset-y-0 left-[var(--left)] z-[1] w-0.5 bg-destructive"
        style={{ "--left": todayLeft(chart.todayCol, chart.range) } as CSSProperties}
      />
    )}
  </div>
);

interface BarProps {
  span: ChartSpan;
  range: ChartRange;
  state: SitePhaseState;
  className?: string;
  children?: ReactNode;
  testId?: string;
  title?: string;
  hidden?: boolean;
}

const Bar = ({ span, range, state, className, children, testId, title, hidden }: BarProps) => (
  <span
    aria-hidden={hidden || undefined}
    className={cn("absolute overflow-hidden whitespace-nowrap rounded-sm px-1.5 text-xs", SPAN_CLASS, TONE_CLASS[state], className)}
    data-testid={testId}
    style={{ "--left": pct(span.from, range), "--w": pct(span.span, range) } as CSSProperties}
    title={title}>
    {children}
  </span>
);

// 짧은 막대 글자 — 칸에 안 들면 잘린 한두 글자 대신 숨기고 읽기 도구·툴팁으로만 준다.
export const BarText = ({ text, days }: { text: string; days: number }) =>
  barLabelFits(text, days) ? <>{text}</> : <span className="sr-only">{text}</span>;

// 현장 줄 막대 — 착공 전은 예정 막대에 「착공 → 준공」, 시공중은 착공~오늘을 끝난 색·오늘 칸·지금 공정 글자.
const SiteBars = ({ row, chart, size }: { row: SiteRow; chart: SiteChartData; size: TrackSize | null }) => {
  const { range, todayCol } = chart;
  const started = row.status === "IN_PROGRESS";
  const labelAfterToday = started && todayCol !== null;
  const rightRoom = (base: string) => (row.endAfterLabel ? `max(${base}, ${END_AFTER_ROOM})` : base);
  // 예정 막대 글자 — ① 막대에 들면 안 ② 바깥 오른쪽에 들면 바깥 ③ 둘 다 아니면 막대 안 말줄임(글자가 안 보이는 경우는 없다).
  const planText = labelAfterToday ? "" : row.label;
  const planEnd = row.plan ? row.plan.from + row.plan.span : 0;
  const planInside = row.plan !== null && barLabelFits(planText, row.plan.span);
  const planOutside = row.plan !== null && planText !== "" && !planInside && barLabelFits(planText, range.days - planEnd);
  // 바깥 글자는 막대 끝 4px 뒤 — 준공 ◆ 가 막대 끝에 붙어 있으면 ◆ 외접 반폭 뒤에서 시작해 겹치지 않는다.
  const planOutLeft =
    row.endCol === null
      ? `calc(${pct(planEnd, range)} + 4px)`
      : `max(calc(${pct(planEnd, range)} + 4px), calc(${endMarkAt(row.endCol, range)} + ${END_MARK_CLEAR}))`;
  const labelBox =
    labelAfterToday && todayCol !== null
      ? progressLabelBox({
          label: row.label,
          todayCol,
          endCol: row.endCol,
          range,
          endAfter: row.endAfterLabel !== null,
          plan: row.plan,
          trackPx: size?.trackPx,
          remPx: size?.remPx,
        })
      : null;
  return (
    <>
      {row.plan && (
        <Bar
          className={cn("top-4 h-6 leading-6", !planInside && !planOutside && "text-ellipsis")}
          range={range}
          span={row.plan}
          state="plan"
          testId={`site-chart-plan-${row.id}`}
          title={planText || undefined}>
          {planOutside ? <span className="sr-only">{planText}</span> : planText}
        </Bar>
      )}
      {planOutside && (
        <span
          aria-hidden
          className="absolute left-[var(--left)] top-4 h-6 whitespace-nowrap text-xs leading-6 text-muted-foreground"
          data-testid={`site-chart-plan-out-${row.id}`}
          style={{ "--left": planOutLeft } as CSSProperties}>
          {planText}
        </span>
      )}
      {row.elapsed && <Bar className="top-4 h-6" range={range} span={row.elapsed} state="done" />}
      {row.todayMark && todayCol !== null && (
        <Bar className="top-4 h-6" range={range} span={row.todayMark} state={row.delayed ? "late" : "now"} />
      )}
      {labelAfterToday && todayCol !== null && row.label && (
        <span
          className={cn(
            "absolute left-[var(--left)] right-[var(--right)] top-4 z-[1] h-6 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold leading-6",
            labelBox?.side === "before" && "text-right",
            row.delayed ? "text-destructive" : "text-primary"
          )}
          data-side={labelBox?.side}
          data-testid={`site-chart-label-${row.id}`}
          data-end={labelBox?.end}
          style={{ "--left": labelBox?.left, "--right": labelBox?.right } as CSSProperties}
          title={row.label}>
          {row.label}
        </span>
      )}
      {!row.plan && !labelAfterToday && row.label && (
        <span
          className="absolute left-[var(--left)] right-[var(--right)] top-4 h-6 overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-6 text-muted-foreground"
          data-side="none"
          data-testid={`site-chart-label-${row.id}`}
          style={{ "--left": "4px", "--right": rightRoom("4px") } as CSSProperties}
          title={row.label}>
          {row.label}
        </span>
      )}
      {row.endCol !== null && (
        <span
          aria-hidden
          className="absolute left-[var(--left)] top-[1.3rem] size-3 -translate-x-1/2 rotate-45 bg-foreground"
          data-testid={`site-chart-end-${row.id}`}
          style={{ "--left": endMarkAt(row.endCol, range) } as CSSProperties}
        />
      )}
      {row.endAfterLabel && (
        <span className="absolute right-0 top-4 z-[1] h-6 whitespace-nowrap bg-card px-1.5 text-xs leading-6 text-muted-foreground">
          {row.endAfterLabel}
        </span>
      )}
    </>
  );
};

// 펼친 공정 줄 막대 — 지연(끝날 넘김)은 계획 구간을 끝난 색 + 넘긴 날 붉은 꼬리, 착수 지연 꼬리(시작일 다음 날부터)는 계획 막대 밑에 깔아 이름을 가리지 않는다(숫자는 오른쪽 칸 문구 한 벌).
export const PhaseBars = ({ phase, chart, siteId }: { phase: SitePhaseRow; chart: SiteChartData; siteId: string }) => {
  const tail = phase.overdue;
  const tailUnder = tail !== null && phase.span !== null && tail.from < phase.span.from + phase.span.span;
  const tailBar = tail && (
    <Bar
      className="top-1 h-4 text-2xs leading-4"
      hidden
      range={chart.range}
      span={tail}
      state="late"
      testId={`site-chart-overdue-${siteId}-${phase.id}`}
      title={phase.stateText}
    />
  );
  return (
    <>
      {tailUnder && tailBar}
      {phase.span && (
        <Bar
          className="top-1 h-4 text-2xs leading-4"
          range={chart.range}
          span={phase.span}
          state={phase.state === "late" && tail && !tailUnder ? "done" : phase.state}
          testId={`site-chart-phase-${siteId}-${phase.id}`}
          title={`${phase.name} ${phase.startDate} ~ ${phase.endDate}`}>
          <BarText days={phase.span.span} text={phase.name} />
        </Bar>
      )}
      {!tailUnder && tailBar}
    </>
  );
};

const dateSlash = (date: string | null): string => (date ? date.slice(5).replace("-", "/") : "미정");

interface Props {
  chart: SiteChartData;
  prevHref: string;
  nextHref: string;
  todayHref: string;
  // 공정 일정을 못 읽었으면 「공정 없음」 대신 「공정 —」.
  phasesFailed?: boolean;
}

// 현장 차트 — 현장마다 착공~준공 막대·오늘 칸·준공 ◆, 줄을 누르면 공정이 펼쳐진다. 라이브러리 없이 칸 비율로 그린다.
const SiteChart = ({ chart, prevHref, nextHref, todayHref, phasesFailed = false }: Props) => {
  const [open, setOpen] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headTrackRef = useRef<HTMLDivElement>(null);
  const size = useTrackSize(headTrackRef);
  const { range, rows, todayCol } = chart;

  // 창을 열거나 옮길 때 — 오늘 선이 보이게 두고, 오늘이 창 밖이면 앞 창의 스크롤을 처음으로 되돌린다.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const marker = el.querySelector<HTMLElement>("[data-testid=site-chart-today-line]");
    const target = chartScrollTarget({
      todayCol,
      markerX: marker ? marker.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft : null,
      nameWidth: el.querySelector<HTMLElement>("[data-testid=site-chart-name-head]")?.offsetWidth ?? 0,
      clientWidth: el.clientWidth,
      scrollWidth: el.scrollWidth,
    });
    if (target !== null) el.scrollLeft = target;
  }, [range.start, todayCol]);
  const maxLoad = Math.max(1, ...chart.load.map((l) => l.count));
  const toggle = (id: string) => setOpen((prev) => (prev === id ? null : id));

  return (
    <div className="flex min-w-0 flex-col gap-y-2" data-testid="site-chart">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1.5">
          <Button asChild aria-label="이전 기간" size="sm" variant="outline">
            <Link href={prevHref} data-testid="site-chart-prev">‹</Link>
          </Button>
          <span className="min-w-36 text-center text-sm font-semibold tabular-nums" data-testid="site-chart-period">
            {chart.period}
          </span>
          <Button asChild aria-label="다음 기간" size="sm" variant="outline">
            <Link href={nextHref} data-testid="site-chart-next">›</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link href={todayHref} data-testid="site-chart-today-link">오늘</Link>
          </Button>
        </div>
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {LEGEND.map((item) => (
            <li key={item.state} className="flex items-center gap-1.5">
              <span className={cn("h-3 w-4 rounded-sm", TONE_CLASS[item.state])} />
              {item.label}
            </li>
          ))}
          <li className="flex items-center gap-1.5">
            <span className="size-3 bg-muted ring-1 ring-inset ring-border" />
            공휴일
          </li>
        </ul>
      </div>

      <SectionCard className="min-w-0" padding="none">
        <div ref={scrollRef} className="overflow-x-auto" data-testid="site-chart-scroll">
          <div className={cn(GRID_CLASS, "text-sm")}>
            <div
              className="sticky left-0 z-[2] flex h-14 items-end border-b border-border bg-card px-3 pb-1.5 text-xs font-medium text-muted-foreground"
              data-testid="site-chart-name-head">
              현장 · 담당
            </div>
            <Track chart={chart} className="h-14" measureRef={headTrackRef} withToday={false}>
              {/* 세 줄 — 달(위) · 「오늘」(가운데) · 주 눈금(아래)이 서로 덮지 않게 줄을 나눈다. */}
              {chart.months.map((month) => (
                <span
                  key={month.col}
                  className={cn("absolute left-[var(--left)] whitespace-nowrap pl-1 text-xs font-semibold", HEAD_ROW_CLASS)}
                  data-testid="site-chart-month"
                  style={
                    {
                      "--top": `${SITE_CHART_HEAD_ROWS.month.top}px`,
                      "--h": `${SITE_CHART_HEAD_ROWS.month.height}px`,
                      "--left": pct(month.col, range),
                    } as CSSProperties
                  }>
                  {month.label}
                </span>
              ))}
              {chart.ticks.map((tick) => (
                <span
                  key={tick.col}
                  className={cn("absolute left-[var(--left)] whitespace-nowrap pl-0.5 text-2xs tabular-nums text-muted-foreground", HEAD_ROW_CLASS)}
                  style={
                    {
                      "--top": `${SITE_CHART_HEAD_ROWS.tick.top}px`,
                      "--h": `${SITE_CHART_HEAD_ROWS.tick.height}px`,
                      "--left": pct(tick.col, range),
                    } as CSSProperties
                  }>
                  {tick.label}
                </span>
              ))}
              {todayCol !== null && (
                <>
                  <span
                    className="absolute bottom-0 left-[var(--left)] top-6 z-[1] w-0.5 bg-destructive"
                    data-testid="site-chart-today-line"
                    style={{ "--left": todayLeft(todayCol, range) } as CSSProperties}
                  />
                  <span
                    className={cn(
                      "absolute left-[var(--left)] right-[var(--right)] z-[1] whitespace-nowrap bg-card px-1 text-2xs font-semibold tabular-nums text-destructive",
                      HEAD_ROW_CLASS
                    )}
                    data-side={todaySideOf(todayCol, range.days)}
                    data-testid="site-chart-today"
                    style={
                      {
                        "--top": `${SITE_CHART_HEAD_ROWS.today.top}px`,
                        "--h": `${SITE_CHART_HEAD_ROWS.today.height}px`,
                        "--left": todaySideOf(todayCol, range.days) === "before" ? "auto" : `calc(${pct(todayCol + 0.5, range)} + 2px)`,
                        "--right": todaySideOf(todayCol, range.days) === "before" ? `calc(${pct(range.days - todayCol - 0.5, range)} + 2px)` : "auto",
                      } as CSSProperties
                    }>
                    오늘 {addDays(range.start, todayCol).slice(5)}
                  </span>
                </>
              )}
            </Track>
            <div
              className={cn(
                "h-14 items-end justify-end border-b border-border px-3 pb-1.5 text-xs font-medium text-muted-foreground",
                RIGHT_CELL
              )}>
              공정 · 준공
            </div>

            {rows.length === 0 && (
              <p className="col-span-2 px-3 py-6 text-center text-sm text-muted-foreground md:col-span-3" data-testid="site-chart-empty">
                이 기간에 그릴 현장이 없습니다.
              </p>
            )}

            {rows.map((row) => {
              const expanded = open === row.id;
              const countText =
                row.phaseTotal > 0 ? `공정 ${row.phaseDone}/${row.phaseTotal}` : phasesFailed ? `공정 ${EMPTY_MARK}` : "공정 없음";
              return (
                <div key={row.id} className="contents" data-testid={`site-chart-row-${row.id}`}>
                  <div className="sticky left-0 z-[2] flex min-h-14 min-w-0 items-center gap-x-2 border-b border-border bg-card py-1 pl-1 pr-2 md:gap-x-1 md:py-0">
                    <Button
                      aria-controls={`site-chart-phases-${row.id}`}
                      aria-expanded={expanded}
                      aria-label={`${row.name} 공정 ${expanded ? "접기" : "펼치기"}`}
                      data-testid={`site-chart-toggle-${row.id}`}
                      disabled={row.phaseTotal === 0}
                      size="icon-xs"
                      variant="ghost"
                      onClick={() => toggle(row.id)}>
                      {expanded ? (
                        <ChevronDown aria-hidden className="text-muted-foreground" data-testid="site-chart-chevron-down" />
                      ) : (
                        <ChevronRight aria-hidden className="text-muted-foreground" data-testid="site-chart-chevron-right" />
                      )}
                    </Button>
                    <span className="flex min-w-0 flex-col">
                      {/* 폰(md 미만) 이름 칸은 7rem 이라 한 줄 말줄임이면 이름이 잘린다 — 두 줄까지 보인다. */}
                      <Link
                        className="line-clamp-2 break-words break-keep font-semibold hover:underline md:line-clamp-none md:truncate"
                        href={`/app/projects/${row.id}`}
                        title={row.name}>
                        {row.name}
                      </Link>
                      <span className="truncate text-xs text-muted-foreground">
                        {row.assignee} · {row.customer} 고객
                      </span>
                      <span
                        className={cn(
                          "whitespace-normal break-keep text-2xs leading-4 tabular-nums md:hidden",
                          row.delayed ? "text-destructive" : "text-muted-foreground"
                        )}
                        data-testid={`site-chart-phone-meta-${row.id}`}>
                        {`${countText} · ${row.endText}`}
                      </span>
                    </span>
                  </div>
                  <Track chart={chart} className="min-h-14" withToday onClick={row.phaseTotal > 0 ? () => toggle(row.id) : undefined}>
                    <SiteBars chart={chart} row={row} size={size} />
                  </Track>
                  <div
                    className={cn("min-h-14 flex-col items-end justify-center gap-y-0.5 border-b border-border px-3 text-right", RIGHT_CELL)}>
                    <span
                      className={cn("whitespace-nowrap text-xs font-medium tabular-nums", row.delayed && "text-destructive")}
                      data-testid={`site-chart-phase-count-${row.id}`}>
                      {countText}
                    </span>
                    <span className="whitespace-nowrap text-2xs tabular-nums text-muted-foreground">{row.endText}</span>
                  </div>

                  {expanded && (
                    <div className="contents" id={`site-chart-phases-${row.id}`}>
                      {row.phases.map((phase) => (
                        <div key={phase.id} className="contents" data-testid={`site-chart-phase-row-${phase.id}`}>
                          <div className="sticky left-0 z-[2] flex h-7 min-w-0 items-center gap-x-2 border-b border-border bg-card pl-3 pr-2 md:pl-8">
                            <span className="min-w-0 truncate text-xs">{phase.name}</span>
                            <span className="ml-auto hidden shrink-0 whitespace-nowrap text-2xs tabular-nums text-muted-foreground md:inline">
                              {dateSlash(phase.startDate)} – {dateSlash(phase.endDate)}
                            </span>
                            {phase.stateText && (
                              <span className={cn("ml-auto shrink-0 whitespace-nowrap text-2xs md:hidden", STATE_TEXT_CLASS[phase.state])}>
                                {phase.stateText}
                              </span>
                            )}
                          </div>
                          <Track chart={chart} className="h-7 bg-accent/30" withToday>
                            <PhaseBars chart={chart} phase={phase} siteId={row.id} />
                          </Track>
                          <div
                            className={cn(
                              "h-7 items-center justify-end whitespace-nowrap border-b border-border px-3 text-2xs",
                              RIGHT_CELL,
                              STATE_TEXT_CLASS[phase.state]
                            )}>
                            {phase.stateText}
                          </div>
                        </div>
                      ))}
                      {row.unscheduled > 0 && (
                        <p className="col-span-2 border-b border-border py-1 pl-3 text-xs text-muted-foreground md:col-span-3 md:pl-8">
                          일정 미정 공정 {row.unscheduled}개 — 날짜가 없어 막대를 그리지 않았습니다.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="sticky left-0 z-[2] flex h-16 flex-col justify-center gap-y-0.5 bg-card px-3">
              <span className="text-xs font-semibold">동시 진행 현장</span>
              <span className="text-2xs text-muted-foreground">주별 · 막대 위 숫자 = 곳</span>
            </div>
            <div className="relative h-16" data-testid="site-chart-load">
              {chart.load.map((week) => (
                <span
                  key={week.from}
                  className={cn(
                    "absolute bottom-2 left-[var(--left)] h-[var(--h)] w-[var(--w)] rounded-t-sm",
                    week.busy ? "bg-warning-bg ring-1 ring-inset ring-warning" : "bg-accent"
                  )}
                  style={
                    {
                      "--left": `calc(${pct(week.from, range)} + 2px)`,
                      "--w": `calc(${pct(week.span, range)} - 4px)`,
                      "--h": `${Math.max((week.count / maxLoad) * 2, 0.125)}rem`,
                    } as CSSProperties
                  }>
                  <span
                    className={cn(
                      "absolute inset-x-0 -top-4 text-center text-2xs font-semibold tabular-nums",
                      week.busy ? "text-warning" : "text-muted-foreground"
                    )}>
                    {week.count}
                  </span>
                </span>
              ))}
            </div>
            <div className={RIGHT_CELL} />
          </div>
        </div>
      </SectionCard>

      <p className="text-xs text-muted-foreground">
        현장 줄을 누르면 공정이 펼쳐집니다 · 유지보수·완료 현장은 공정이 없어 빠집니다 · 담당·상태 칩은 목록 보기와 같이 갑니다
      </p>
    </div>
  );
};

export default SiteChart;
